<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.bulletins', 2),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.store" method="post" @submit.prevent="submit">
            <TitleFieldsFormSection>
                <template v-slot:title>
                    {{ $t('messages.information') }}
                </template>
                <div class="md:grid md:grid-cols-6 md:gap-6">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.title')" for="title" />
                            <FormInput
                                required
                                id="title"
                                v-model.trim="form.title"
                                name="title"
                                type="text"
                                max="60"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.title" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.published_at')" for="published_at" />
                            <FormInput
                                required
                                id="published_at"
                                v-model="form.published_at"
                                name="published_at"
                                type="datetime-local"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.published_at" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-3">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.unpublished_at')" for="unpublished_at" />
                            <FormInput
                                required
                                id="unpublished_at"
                                v-model="form.unpublished_at"
                                name="unpublished_at"
                                type="datetime-local"
                                class="mt-1"
                            />
                            <FormError :value="form.errors.unpublished_at" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.body')" for="body" />
                            <Editor id="body" v-model="form.body" name="body" class="mt-2" />
                            <FormError :value="form.errors.body" class="mt-2" />
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.links', 2)" for="links" />
                        <LinkBatchCreate v-model="form.links" :links-input="[]" />
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.image')" for="image" />
                        <input
                            id="image"
                            ref="imageFileInput"
                            @input="handleImageInput"
                            name="image"
                            type="file"
                            accept="image/*"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.image" class="mt-2" />
                    </div>
                    <div class="w-1/2 relative" v-if="preview">
                        <div class="absolute w-full top-0 right-0">
                            <button type="button" @click="removeImage">
                                <svg
                                    class="w-6 h-6 text-red-300"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                            </button>
                        </div>
                        <img :src="preview" alt="" />
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div>
                        {{ $tc('messages.attachments', 2) }}
                    </div>
                    <div>
                        <div class="mt-1" v-if="form.attachments.length < 3">
                            <FormLabel :value="$tc('messages.attachments', 1)" for="attachment" />
                            <input
                                id="attachment"
                                ref="attachmentFileInput"
                                @input="handleNewAttachment"
                                name="attachment"
                                type="file"
                                accept="application/pdf,.jpg,.jpeg"
                                class="mt-1 w-full"
                            />
                            <FormError :value="form.errors.attachments" class="mt-2" />
                        </div>
                    </div>
                    <div class="mt-1">
                        <AttachmentsUploadPreviews
                            @remove="removeAttachment"
                            :attachments="form.attachments"
                            :max-bytes-size="12 * 1024 * 1024"
                        />
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <FormLabel :value="$t('messages.groups')">
                        <SelectAllNoneButtons
                            :max-count="2"
                            :selected-count="1"
                            @select-all="selectAllGroups()"
                            @select-none="form.group_ids = []"
                            class="inline-flex"
                        />
                    </FormLabel>
                    <div class="mt-1 rounded-md shadow-sm">
                        <label class="ml-1 mr-3" v-for="group in options.groups" :key="group.value">
                            <input
                                name="group_ids[]"
                                type="checkbox"
                                :value="group.value"
                                v-model="form.group_ids"
                                class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                            />
                            <span class="inline-block text-sm leading-5 font-medium text-gray-700">
                                {{ group.text }}
                            </span>
                        </label>
                    </div>
                </div>
            </TitleFieldsFormSection>

            <div class="flex justify-end mt-5">
                <SubmitFormButton :button-text="$t('messages.save')" :processing="form.processing" />
            </div>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import Editor from '@/components/Forms/RichTextEditor/Editor.vue';
import SelectAllNoneButtons from '@/components/SelectAllNoneButtons.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import LinkBatchCreate from '@/components/pageSpecific/LinkBatchCreate.vue';
import AttachmentsUploadPreviews from '@/components/AttachmentsUploadPreviews.vue';

export default {
    components: {
        AttachmentsUploadPreviews,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        Editor,
        SelectAllNoneButtons,
        SubmitFormButton,
        LinkBatchCreate,
    },
    layout: Layout,
    name: 'Create',
    props: ['meta', 'options'],
    data() {
        return {
            preview: null,
            form: this.$inertia.form({
                title: '',
                body: '',
                published_at: '',
                unpublished_at: '',
                group_ids: [],
                image: null,
                attachments: [],
            }),
        };
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.store);
        },
        selectAllGroups() {
            this.form.group_ids = this.options.groups.map((g) => g.value);
        },
        handleImageInput(event) {
            const file = event.target.files[0];
            this.form.image = file;
            const reader = new FileReader();
            reader.addEventListener('load', (event) => (this.preview = event.target.result));
            reader.readAsDataURL(file);
            this.$refs.imageFileInput.value = null;
        },
        removeImage() {
            this.preview = null;
            this.form.image = null;
        },
        handleNewAttachment(event) {
            const file = event.target.files[0];
            this.form.attachments.push(file);
            this.$refs.attachmentFileInput.value = null;
        },
        removeAttachment(index) {
            this.form.attachments.splice(index, 1);
        },
    },
};
</script>
